import React, { useState, useEffect, useCallback } from 'react';
import { Mail, CheckCircle, Eye, MousePointerClick, AlertTriangle, UserMinus, ShieldAlert, RefreshCw } from 'lucide-react';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

const PERIODOS = [
    { id: '7d', label: '7 días' },
    { id: '30d', label: '30 días' },
    { id: '90d', label: '90 días' },
    { id: '12m', label: '12 meses' }
];

// Clases completas (no interpoladas) para que Tailwind no las elimine en el build
const COLORES = {
    blue: ['bg-blue-50 border-blue-100', 'text-blue-700', 'text-blue-400', 'text-blue-900'],
    green: ['bg-green-50 border-green-100', 'text-green-700', 'text-green-400', 'text-green-900'],
    purple: ['bg-purple-50 border-purple-100', 'text-purple-700', 'text-purple-400', 'text-purple-900'],
    indigo: ['bg-indigo-50 border-indigo-100', 'text-indigo-700', 'text-indigo-400', 'text-indigo-900'],
    red: ['bg-red-50 border-red-100', 'text-red-700', 'text-red-400', 'text-red-900'],
    orange: ['bg-orange-50 border-orange-100', 'text-orange-700', 'text-orange-400', 'text-orange-900'],
    gray: ['bg-gray-50 border-gray-200', 'text-gray-700', 'text-gray-400', 'text-gray-900']
};

const Tarjeta = ({ titulo, valor, subtitulo, icono: Icono, color }) => {
    const [caja, texto, icono, numero] = COLORES[color] || COLORES.gray;
    return (
        <div className={`${caja} p-4 rounded-lg border`}>
            <div className="flex items-center justify-between">
                <p className={`text-sm ${texto} font-semibold`}>{titulo}</p>
                <Icono size={20} className={icono} />
            </div>
            <p className={`text-2xl font-bold ${numero} mt-1`}>{valor}</p>
            {subtitulo && <p className={`text-xs ${texto} mt-1`}>{subtitulo}</p>}
        </div>
    );
};

const formatearFecha = (fecha, resolucion) => {
    if (!fecha) return '-';
    const d = new Date(fecha);
    if (isNaN(d)) return fecha;
    return resolucion === 'month'
        ? d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
        : d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const EmailStatsPanel = () => {
    const [periodo, setPeriodo] = useState('30d');
    const [datos, setDatos] = useState(null);
    const [seguimiento, setSeguimiento] = useState([]);
    const [error, setError] = useState(null);
    const [cargando, setCargando] = useState(true);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        try {
            const [resStats, resSeg] = await Promise.all([
                fetch(`${API_BASE}/api/informes/email-stats?periodo=${periodo}`, { headers: getAuthorizedHeaders() }),
                fetch(`${API_BASE}/api/informes/email-seguimiento`, { headers: getAuthorizedHeaders() })
            ]);
            const jsonStats = await resStats.json().catch(() => ({}));
            if (resStats.ok && jsonStats.success) setDatos(jsonStats.data);
            else { setDatos(null); setError(jsonStats.error || 'No se pudieron cargar las estadísticas de email.'); }

            const jsonSeg = await resSeg.json().catch(() => ({}));
            setSeguimiento(resSeg.ok && jsonSeg.success ? jsonSeg.data : []);
        } catch (e) {
            setError('No se pudo conectar con el servidor.');
        } finally {
            setCargando(false);
        }
    }, [periodo]);

    useEffect(() => { cargar(); }, [cargar]);

    const t = datos?.totales || {};
    const tasas = datos?.tasas || {};

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <Mail size={20} className="text-primary-600 shrink-0" /> Rendimiento de Email
                </h3>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    {PERIODOS.map(p => (
                        <button
                            key={p.id}
                            onClick={() => setPeriodo(p.id)}
                            className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md border ${periodo === p.id ? 'bg-primary-600 text-white border-primary-600' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:border-gray-600'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    <button onClick={cargar} title="Actualizar" className="p-2 rounded-md border bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50">
                        <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {cargando && !datos ? (
                <p className="text-gray-500 text-center py-6">Cargando estadísticas de email...</p>
            ) : error ? (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm">{error}</div>
            ) : datos && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        <Tarjeta titulo="Enviados" valor={t.aceptados || 0} icono={Mail} color="blue" />
                        <Tarjeta titulo="Entregados" valor={t.entregados || 0} subtitulo={`${tasas.entrega}% de enviados`} icono={CheckCircle} color="green" />
                        <Tarjeta titulo="Abiertos" valor={t.abiertos_unicos || t.abiertos || 0} subtitulo={`${tasas.apertura}% de entregados`} icono={Eye} color="purple" />
                        <Tarjeta titulo="Clics" valor={t.clics_unicos || t.clics || 0} subtitulo={`${tasas.clic}% de entregados`} icono={MousePointerClick} color="indigo" />
                        <Tarjeta titulo="Rebotes" valor={t.fallidos_permanentes || 0} subtitulo={`${tasas.rebote}% de enviados`} icono={AlertTriangle} color="red" />
                        <Tarjeta titulo="Bajas" valor={t.bajas || 0} subtitulo={`${tasas.baja}% de entregados`} icono={UserMinus} color="orange" />
                        <Tarjeta titulo="Spam" valor={t.spam || 0} subtitulo={`${tasas.spam}% de entregados`} icono={ShieldAlert} color="gray" />
                    </div>

                    <div className="overflow-x-auto bg-white rounded-lg border">
                        <table className="w-full text-sm text-left text-gray-700">
                            <thead className="bg-gray-50 text-gray-900 font-semibold">
                                <tr>
                                    <th className="px-4 py-3">{datos.resolucion === 'month' ? 'Mes' : 'Día'}</th>
                                    <th className="px-4 py-3 text-right">Enviados</th>
                                    <th className="px-4 py-3 text-right">Entregados</th>
                                    <th className="px-4 py-3 text-right">Abiertos</th>
                                    <th className="px-4 py-3 text-right">Clics</th>
                                    <th className="px-4 py-3 text-right">Rebotes</th>
                                    <th className="px-4 py-3 text-right">Fallos temp.</th>
                                    <th className="px-4 py-3 text-right">Bajas</th>
                                    <th className="px-4 py-3 text-right">Spam</th>
                                </tr>
                            </thead>
                            <tbody>
                                {datos.serie.filter(p => p.aceptados || p.entregados || p.fallidos_permanentes || p.fallidos_temporales || p.abiertos || p.clics).map(p => (
                                    <tr key={p.fecha} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2 capitalize">{formatearFecha(p.fecha, datos.resolucion)}</td>
                                        <td className="px-4 py-2 text-right font-medium">{p.aceptados}</td>
                                        <td className="px-4 py-2 text-right text-green-700">{p.entregados}</td>
                                        <td className="px-4 py-2 text-right">{p.abiertos_unicos || p.abiertos}</td>
                                        <td className="px-4 py-2 text-right">{p.clics_unicos || p.clics}</td>
                                        <td className="px-4 py-2 text-right text-red-600">{p.fallidos_permanentes}</td>
                                        <td className="px-4 py-2 text-right text-amber-600">{p.fallidos_temporales}</td>
                                        <td className="px-4 py-2 text-right">{p.bajas}</td>
                                        <td className="px-4 py-2 text-right">{p.spam}</td>
                                    </tr>
                                ))}
                                {datos.serie.every(p => !p.aceptados && !p.entregados && !p.fallidos_permanentes && !p.fallidos_temporales) && (
                                    <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">No hay envíos de email en este periodo.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {seguimiento.length > 0 && (
                <>
                    <h4 className="text-md font-semibold text-gray-800 mt-6">Seguimiento por comunicación</h4>
                    <div className="overflow-x-auto bg-white rounded-lg border">
                        <table className="w-full text-sm text-left text-gray-700">
                            <thead className="bg-gray-50 text-gray-900 font-semibold">
                                <tr>
                                    <th className="px-4 py-3">Comunicación</th>
                                    <th className="px-4 py-3">Fecha</th>
                                    <th className="px-4 py-3 text-right">Destinatarios</th>
                                    <th className="px-4 py-3 text-right">Entregados</th>
                                    <th className="px-4 py-3 text-right">Abiertos</th>
                                    <th className="px-4 py-3 text-right">Con clic</th>
                                    <th className="px-4 py-3 text-right">Rebotes / Fallos</th>
                                    <th className="px-4 py-3 text-right">Bajas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {seguimiento.map(s => (
                                    <tr key={s.id} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2">{s.nombre_campana || s.plantilla_nombre || `Comunicación #${s.id}`}</td>
                                        <td className="px-4 py-2">{s.fecha_enviada ? new Date(s.fecha_enviada).toLocaleString('es-ES') : '-'}</td>
                                        <td className="px-4 py-2 text-right font-medium">{s.total}</td>
                                        <td className="px-4 py-2 text-right text-green-700">{s.entregados}</td>
                                        <td className="px-4 py-2 text-right">{s.abiertos}</td>
                                        <td className="px-4 py-2 text-right">{s.con_clic}</td>
                                        <td className="px-4 py-2 text-right text-red-600">{Number(s.rebotados) + Number(s.fallidos)}</td>
                                        <td className="px-4 py-2 text-right">{s.bajas}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default EmailStatsPanel;
